'use client'

import { useState } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { getWhatsAppUrl, WHATSAPP_FAQ_MESSAGE } from '../../../../config/contact';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);
  
  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  
  const faqs = [
    {
      question: "Is there a big 'setup' cost?",
      answer: "Not at all. We believe in complete transparency. There are zero setup charges and no hidden fees whatsoever."
    },
    {
      question: "Does this need special hardware?",
      answer: "No special hardware required! Your existing smartphone camera is all you need to get started. We've designed ScanMaadi to work with technology you already have."
    },
    {
      question: "Are daily menu changes possible?",
      answer: "Absolutely! Updates are instant via our easy-to-use dashboard. Change prices, add new items, or mark dishes as unavailable—all in real-time without any delays."
    },
    {
      question: "Is any app required for customers?",
      answer: "No apps needed. Your customers simply scan the QR code and view your menu directly in their browser. This means no friction and immediate access to your latest offerings."
    },
    {
      question: "Can I use my own design for QR?",
      answer: "Definitely! We offer customization options to match your restaurant's branding. Contact us for white label solutions and personalized QR code designs."
    }
  ];
  
  // Decorative elements for visual interest
  const Decoration = () => (
    <>
      <div className="absolute -top-24 -right-20 w-72 h-72 bg-primary-100 rounded-full blur-3xl opacity-30" />
      <div className="absolute -bottom-24 -left-20 w-72 h-72 bg-accent-100 rounded-full blur-3xl opacity-30" />
    </>
  );
  
  return (
    <div id="faq" className="bg-white py-24 sm:py-32 relative overflow-hidden">
      <Decoration />
      
      <div className="mx-auto max-w-3xl px-6 lg:px-8 relative z-10">
        <div className="mb-16 text-center">
          <h2 className="text-base font-semibold text-primary-500 tracking-wide uppercase">
            Support
          </h2>
          <p className="mt-2 text-4xl font-extrabold tracking-tight text-text-700 sm:text-5xl">
            Frequently Asked <span className="text-accent-500">Questions</span>
          </p>
          <p className="mt-4 max-w-2xl text-xl text-text-500 mx-auto">
            Everything you need to know about ScanMaadi's digital menu solution
          </p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="divide-y divide-background-200">
            {faqs.map((faq, index) => (
              <div key={index} className="relative">
                {/* Background gradient effect when open */}
                {openIndex === index && (
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-50 to-accent-50 opacity-60"></div>
                )}
                
                {/* FAQ Item */}
                <div className="relative">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-8 py-6 flex items-center justify-between text-left focus:outline-none"
                  >
                    <span className="text-lg font-medium text-text-700">{faq.question}</span>
                    <span className="ml-6 flex-shrink-0">
                      {openIndex === index ? (
                        <ChevronUpIcon className="h-5 w-5 text-primary-500" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-text-400" />
                      )}
                    </span>
                  </button>
                  
                  {/* Answer content */}
                  <div 
                    className={`px-8 pb-6 transition-all duration-300 ease-in-out overflow-hidden ${
                      openIndex === index ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="text-text-600">{faq.answer}</p>
                  </div>
                </div>
                
                {/* Subtle indicator for open state */}
                {openIndex === index && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-500 to-accent-500"></div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Support callout box */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center justify-center p-6 bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg transform transition duration-500 hover:scale-105">
            <div className="text-white">
              <p className="text-lg font-bold mb-2">Still have questions?</p>
              <p className="mb-4">Our friendly team is just a message away.</p>
              <a
                href={getWhatsAppUrl(WHATSAPP_FAQ_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-xl text-primary-700 bg-white hover:bg-background-50 transition-all duration-300"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}